import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Container,
  Title,
  Text,
  Table,
  Loader,
  Center,
  Tooltip,
  ActionIcon,
  Modal,
  Group,
  Button,
  Badge,
} from "@mantine/core";
import {
  IconDownload,
  IconFile,
  IconFileText,
  IconFileZip,
  IconFileCode,
  IconEye,
} from "@tabler/icons-react";

import "./styles.scss";
import { useParams } from "react-router-dom";
import { usePerformRequest } from "../../Lib/usePerformRequest";
import { NonPaginatedResponse } from "../../Lib/responses";
import { MaterialProps } from "../../Lib/types";
import { Endpoints } from "../../Lib/endpoints";

function getFileIcon(filename: string) {
  const ext = filename.split(".").pop()?.toLowerCase();
  if (["pdf"].includes(ext!)) return <IconFile color="#e63946" />;
  if (["zip", "rar", "7z"].includes(ext!)) return <IconFileZip color="#f59e42" />;
  if (["jpg", "jpeg", "png", "gif", "bmp", "svg", "webp"].includes(ext!))
    return <IconFile color="#38bdf8" />;
  if (
    [
      "js",
      "ts",
      "tsx",
      "py",
      "java",
      "cpp",
      "c",
      "cs",
      "rb",
      "go",
      "php",
      "html",
      "css",
      "json",
    ].includes(ext!)
  )
    return <IconFileCode color="#6366f1" />;
  if (["txt", "md", "rtf"].includes(ext!)) return <IconFileText color="#64748b" />;
  return <IconFile color="#a78bfa" />;
}

const LessonMaterials: React.FC = () => {
  const { lessonID } = useParams();
  const { t } = useTranslation();
  const [selectedMaterial, setSelectedMaterial] = useState<MaterialProps | null>(null);
  const [modalOpened, setModalOpened] = useState(false);

  const { data: materials, isLoading } = usePerformRequest<NonPaginatedResponse<MaterialProps[]>>({
    method: Endpoints.GetLessonMaterials(lessonID as string).method,
    route: Endpoints.GetLessonMaterials(lessonID as string).route,
  });

  const handleMaterialClick = (material: MaterialProps) => {
    setSelectedMaterial(material);
    setModalOpened(true);
  };

  const closeModal = () => {
    setModalOpened(false);
    setSelectedMaterial(null);
  };

  const getFileType = (filename: string) => {
    const ext = filename.split(".").pop()?.toLowerCase();
    if (["ppt", "pptx"].includes(ext!)) return t("ui.fileTypes.powerpoint");
    if (["doc", "docx"].includes(ext!)) return t("ui.fileTypes.word");
    if (["xls", "xlsx"].includes(ext!)) return t("ui.fileTypes.excel");
    if (["pdf"].includes(ext!)) return t("ui.fileTypes.pdf");
    if (["zip", "rar", "7z"].includes(ext!)) return t("ui.fileTypes.archive");
    if (["jpg", "jpeg", "png", "gif", "bmp", "svg", "webp"].includes(ext!))
      return t("ui.fileTypes.image");
    if (
      [
        "js",
        "ts",
        "tsx",
        "py",
        "java",
        "cpp",
        "c",
        "cs",
        "rb",
        "go",
        "php",
        "html",
        "css",
        "json",
      ].includes(ext!)
    )
      return t("ui.fileTypes.code");
    if (["txt", "md", "rtf"].includes(ext!)) return t("ui.fileTypes.text");
    return t("ui.fileTypes.file");
  };

  return (
    <Container size="lg" className="lesson-materials-container">
      <Title order={2} className="materials-title" mb="xl" ta="center">
        {t("lessonMaterials.title")}
      </Title>

      {isLoading ? (
        <Center h={300}>
          <Loader size="xl" color="violet" />
        </Center>
      ) : !materials?.data || materials.data.length === 0 ? (
        <Center h={220}>
          <Text size="lg" c="dimmed">
            {t("lessonMaterials.noMaterials")}
          </Text>
        </Center>
      ) : (
        <div className="materials-table-container">
          <Table className="materials-table" striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("lessonMaterials.materialTitle")}</Table.Th>
                <Table.Th>{t("lessonMaterials.description")}</Table.Th>
                <Table.Th>{t("lessonMaterials.filesCount")}</Table.Th>
                <Table.Th>{t("lessonMaterials.actions")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {materials.data.map((material) => (
                <Table.Tr
                  key={material.id}
                  className="material-row"
                  onClick={() => handleMaterialClick(material)}
                >
                  <Table.Td className="material-title-cell">
                    <Text fw={600} size="md">
                      {material.title}
                    </Text>
                  </Table.Td>
                  <Table.Td className="material-description-cell">
                    <Text size="sm" c="dimmed" lineClamp={2}>
                      {material.description}
                    </Text>
                  </Table.Td>
                  <Table.Td className="files-count-cell">
                    <Badge color="violet" variant="light" size="sm">
                      {material.files ? material.files.length : 0} {t("lessonMaterials.files")}
                    </Badge>
                  </Table.Td>
                  <Table.Td className="actions-cell">
                    <Tooltip label={t("lessonMaterials.viewFiles")} withArrow>
                      <ActionIcon
                        color="violet"
                        size="md"
                        variant="light"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMaterialClick(material);
                        }}
                      >
                        <IconEye size={18} />
                      </ActionIcon>
                    </Tooltip>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </div>
      )}

      {/* Files Modal */}
      <Modal
        opened={modalOpened}
        onClose={closeModal}
        title={
          <Group gap="sm">
            <Text fw={700} size="lg">
              {selectedMaterial?.title}
            </Text>
            <Badge color="violet" variant="light">
              {selectedMaterial?.files ? selectedMaterial.files.length : 0}{" "}
              {t("lessonMaterials.files")}
            </Badge>
          </Group>
        }
        size="lg"
        centered
        className="files-modal"
      >
        {selectedMaterial && (
          <div className="files-modal-content">
            <Text size="sm" c="dimmed" mb="lg">
              {selectedMaterial.description}
            </Text>

            {selectedMaterial.files && selectedMaterial.files.length > 0 ? (
              <div className="files-list">
                {selectedMaterial.files.map((file, index) => (
                  <div key={index} className="file-item">
                    <Group justify="space-between" align="center" wrap="nowrap">
                      <Group gap="sm" align="center">
                        {getFileIcon(file)}
                        <div className="file-info">
                          <Text size="sm" fw={600} className="file-name">
                            {file.split("/").pop()}
                          </Text>
                          <Group gap="xs">
                            <Badge size="xs" variant="outline" color="gray">
                              {getFileType(file)}
                            </Badge>
                          </Group>
                        </div>
                      </Group>

                      <Tooltip label={t("lessonMaterials.download")} withArrow>
                        <ActionIcon
                          component="a"
                          href={file}
                          download
                          target="_blank"
                          rel="noopener noreferrer"
                          color="violet"
                          size="md"
                          variant="filled"
                          className="download-btn-modal"
                        >
                          <IconDownload size={18} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </div>
                ))}
              </div>
            ) : (
              <Center h={100}>
                <Text size="md" c="dimmed">
                  {t("lessonMaterials.noFilesAttached")}
                </Text>
              </Center>
            )}

            <Group justify="flex-end" mt="xl">
              <Button variant="subtle" onClick={closeModal}>
                {t("lessonMaterials.close")}
              </Button>
            </Group>
          </div>
        )}
      </Modal>
    </Container>
  );
};

export default LessonMaterials;
